/**
 * Recursively removes all __typename properties from an object or array
 * @param data - The data structure to sanitize
 * @returns A new data structure with all __typename properties removed
 */
export const removeTypenames = <T>(data: T): T => {
  // Handle null or non-object values
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => removeTypenames(item)) as unknown as T;
  }

  // Handle objects
  const result: Record<string, any> = {};

  // Copy all properties except __typename
  Object.entries(data).forEach(([key, value]) => {
    if (key !== '__typename') {
      // Recursively clean nested objects/arrays
      result[key] = removeTypenames(value);
    }
  });

  return result as T;
};

/**
 * Sanitizes a draft object by removing all __typename properties
 * @param draft - The draft object to sanitize
 * @returns A sanitized version of the draft object
 */
export const sanitizeDraft = <T extends Record<string, any>>(draft: T): T => {
  if (!draft) return null as unknown as T;
  return removeTypenames(draft);
};

export const removeTypenameLegacy = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;

  const { __typename, ...rest } = obj;

  return Object.keys(rest).reduce((acc, key) => {
    acc[key] = removeTypenameLegacy(rest[key]); // Recursively process nested objects
    return acc;
  }, {} as any);
};
